import {
  AccommodationHotelsQuery,
  AccommodationPlaceRoomBlockStatus,
  AccommodationPlaceSourceOfData,
  AccommodationPlaceSourceType,
  AccommodationPlaceType,
  AddAccommodationPlaceV2Request,
  FindMatchingAccommodationCatalogPropertyQuery,
  UpdateAccommodationPlaceV2Request
} from '@graphql/generated';
import { AccommodationItemList, TravelMapBlockType } from '../TravelMap.types';
import { CustomPropertyFormFields } from '../pages/CustomProperty/CustomPropertyForm.types';
import { HotelFormFields } from '../pages/Hotel/HotelForm.types';
import { LocationInputV2Response } from '@shared/components/LocationInputV2/LocationInputV2.types';
import { Location } from '@shared/components/LocationInput/LocationInput.controller';
import { PreDefinedGooglePlace } from '@shared/utils/hooks/usePreDefinedGooglePlaces';
import { PlaceInfoAdvanced } from '@shared/core/places/places';
import { isNull } from 'lodash-es';
import { formatDateForTravel } from '@shared/utils/dateHelpers';

interface DetailAddressArgs {
  address?: string | null;
  city?: string | null;
  state?: string | null;
  postalCode?: string | null;
  country?: string | null;
}

export const getHotelAddress = ({ address, city, state, postalCode, country }: DetailAddressArgs) => {
  const fullAddress = `${address ?? ''}${city ? ', ' + city : ''}${state || postalCode ? ', ' : ''}${state ?? ''}${postalCode ? ' ' + postalCode : ''}${
    country ? ', ' + country : ''
  }`;
  return fullAddress;
};

export const generateAccommodationList: (accommodation: AccommodationHotelsQuery['eventByName'], accommodationsMyOwnLinkEnabled: boolean) => AccommodationItemList[] = (
  accommodation,
  accommodationsMyOwnLinkEnabled
) => {
  return [
    ...(accommodation?.accommodationPlacesV2 || [])
      .filter(accPlaceV2 => isNull(accPlaceV2?.accommodationRoomBlock))
      .map(accPlaceV2 => ({
        id: accPlaceV2.id,
        type: accPlaceV2.type === AccommodationPlaceType.hotel ? TravelMapBlockType.travelMapAccommodationsHotel : TravelMapBlockType.travelMapAccommodationsCustom,
        isFavorite: accPlaceV2.isFavorite || false,
        photoUrl: accPlaceV2.customPhoto?.url || accPlaceV2.photo?.url || '',
        isPhotoHidden: !!accPlaceV2.isPhotoHidden,
        address:
          accPlaceV2.customAddress ||
          getHotelAddress({ address: accPlaceV2.address, city: accPlaceV2.city, state: accPlaceV2.state, postalCode: accPlaceV2.postalCode, country: accPlaceV2.country }) ||
          '',
        cutoffDate: '',
        displayName: accPlaceV2.customName || accPlaceV2.name || '',
        lat: accPlaceV2.lat,
        lng: accPlaceV2.long,
        description: accPlaceV2.description,
        starRating: accPlaceV2?.starRating,
        phone: accPlaceV2.phone,
        customPhone: accPlaceV2.customPhone,
        createdByType: accPlaceV2.createdByType,
        placeId: accPlaceV2.zhId || accPlaceV2.googlePlaceId,
        websiteUrl: accommodationsMyOwnLinkEnabled ? accPlaceV2.customUrl || undefined : accPlaceV2.customUrl || accPlaceV2.url || undefined,
        joyPlaceId: accPlaceV2.joyPlaceId,
        currency: 'USD',
        isFromAccommodationCatalog: accPlaceV2.isFromAccommodationCatalog,
        isJoyManagedRoomBlock: false
      })),
    ...(accommodation?.accommodationPlacesV2 || [])
      .filter(accPlaceV2 => !isNull(accPlaceV2?.accommodationRoomBlock) && accPlaceV2?.accommodationRoomBlock?.status === AccommodationPlaceRoomBlockStatus.active)
      .map(accPlaceV2 => ({
        id: accPlaceV2.id,
        type: TravelMapBlockType.travelMapAccommodationsRoomblock,
        isFavorite: accPlaceV2.isFavorite,
        photoUrl: accPlaceV2.customPhoto?.url || accPlaceV2.photo?.url || '',
        joyPlaceId: accPlaceV2?.joyPlaceId,
        address: accPlaceV2.address || '',
        cutoffDate: accPlaceV2.accommodationRoomBlock?.cutoffDate || '',
        displayName: accPlaceV2.customName || accPlaceV2.name || '',
        isPhotoHidden: false,
        lat: accPlaceV2.lat,
        lng: accPlaceV2.long,
        starRating: accPlaceV2?.starRating,
        createdByType: accPlaceV2.createdByType,
        description: accPlaceV2.description || '',
        price: accPlaceV2.accommodationRoomBlock?.pricePerNight,
        strikeoutPrice: accPlaceV2.accommodationRoomBlock?.strikeoutPricePerNight ?? undefined,
        websiteUrl: accPlaceV2.accommodationRoomBlock?.bookingUrl ?? undefined,
        numberOfRoomsReserved: accPlaceV2.accommodationRoomBlock?.numberOfRoomsReserved ?? 0,
        checkInDate: accPlaceV2.accommodationRoomBlock?.checkInDate ?? '',
        checkOutDate: accPlaceV2.accommodationRoomBlock?.checkOutDate ?? '',
        isJoyManagedRoomBlock: Boolean(accPlaceV2.accommodationRoomBlock?.isJoyManagedRoomBlock),
        // TODO: Get currency from BE
        currency: 'USD'
      }))
  ];
};

/**
 * Reset form fields when change hotel from autocomplete
 */
export const resetHotelFormFields = (updateFormValues: CustomPropertyFormFields | HotelFormFields, isHotelForm?: boolean) => {
  updateFormValues.customPhotoId = null;
  updateFormValues.photoUrl = null;
  updateFormValues.phone = '';
  updateFormValues.customPhone = '';
  updateFormValues.url = null;
  updateFormValues.customUrl = null;
  updateFormValues.postalCode = '';
  updateFormValues.zhId = '';
  if (isHotelForm) {
    updateFormValues.address = '';
  }
};

/**
 * Get place details from google place and align with hotel form
 */
export const updateHotelFormDataFromGooglePlace = (
  updateFormValues: CustomPropertyFormFields | HotelFormFields,
  location: LocationInputV2Response | Location,
  isHotelForm?: boolean
) => {
  updateFormValues.placeId = location.placeId;
  updateFormValues.googlePlaceId = location.placeId;
  updateFormValues.country = location.country;
  updateFormValues.latitude = location.lat;
  updateFormValues.longitude = location.lng;
  if ('address' in location && location.address && isHotelForm) {
    updateFormValues.location = location.address;
  }
};

/**
 * Get place details from pre-defined place and align with hotel form
 */
export const updateHotelFormDataFromPreDefinedPlace = (
  updateFormValues: CustomPropertyFormFields | HotelFormFields,
  preDefinedPlace: PreDefinedGooglePlace,
  isUpdateCustomUrlLocally?: boolean
) => {
  updateFormValues = { ...updateFormValues, ...preDefinedPlace };
  updateFormValues.customName = preDefinedPlace.name;
  updateFormValues.customPhone = preDefinedPlace.phone;
  updateFormValues.customAddress = preDefinedPlace.formattedAddress || preDefinedPlace.address;
  updateFormValues.googlePlaceId = preDefinedPlace.placeId;
  updateFormValues.location = preDefinedPlace.address;
  updateFormValues.latitude = preDefinedPlace.lat;
  updateFormValues.longitude = preDefinedPlace.lng;
  updateFormValues.url = preDefinedPlace.url;
  if (isUpdateCustomUrlLocally) {
    updateFormValues.isCustomUrl = false;
    updateFormValues.customUrl = preDefinedPlace.url;
  }
};

/**
 * Get place details from zh and align with hotel form
 */
export const updateHotelFormDataFromZhPlace = (updateFormValues: CustomPropertyFormFields | HotelFormFields, place: PlaceInfoAdvanced, isUpdateCustomUrlLocally?: boolean) => {
  updateFormValues.address = place.formattedAddress ?? '';
  updateFormValues.customAddress = place.formattedAddress ?? '';
  updateFormValues.postalCode = place.addressComponents?.find(component => component.types?.includes('postal_code'))?.longText ?? '';
  updateFormValues.phone = place.internationalPhoneNumber ?? '';
  updateFormValues.customPhone = place.internationalPhoneNumber ?? '';
  updateFormValues.url = place.websiteUri ?? '';
  if (isUpdateCustomUrlLocally) {
    updateFormValues.isCustomUrl = false;
    updateFormValues.customUrl = place.websiteUri ?? '';
  }
};

/**
 * Get place details from accommodations catalog property and align with hotel form
 */
export const updateHotelFormDataFromCatalogProperty = (
  updateFormValues: CustomPropertyFormFields | HotelFormFields,
  findMatchingAccommodationCatalogProperty: FindMatchingAccommodationCatalogPropertyQuery['findMatchingAccommodationCatalogProperty'],
  isUpdateCustomUrlLocally?: boolean
) => {
  const accommodationCatalogProperty = findMatchingAccommodationCatalogProperty?.accommodationCatalogProperty;
  if (accommodationCatalogProperty) {
    const address = accommodationCatalogProperty.address;
    updateFormValues.address = getHotelAddress({ ...address, address: address?.street }) ?? '';
    updateFormValues.customAddress = getHotelAddress({ ...address, address: address?.street }) ?? '';
    updateFormValues.postalCode = address?.postalCode ?? '';
    // Don't need this field with new UI, added here to reset from form while update.
    updateFormValues.url = '';
    if (isUpdateCustomUrlLocally) {
      updateFormValues.isCustomUrl = false;
      updateFormValues.customUrl = '';
    }
    updateFormValues.phone = '';
    updateFormValues.customPhone = '';
  }
};

/**
 * Convert accommodation provider id to source of data
 */
export const convertAccommodationProviderIdToSourceOfData = (values: { googlePlaceId?: string | null; joyPlaceId?: string | null; zhId?: string | null }) => {
  let sourceOfData: AccommodationPlaceSourceOfData = { sourceId: values.googlePlaceId || '', sourceType: AccommodationPlaceSourceType.google };
  if (values.joyPlaceId) {
    sourceOfData = { sourceId: values.joyPlaceId, sourceType: AccommodationPlaceSourceType.joyPlace };
  } else if (values.zhId) {
    sourceOfData = { sourceId: values.zhId, sourceType: AccommodationPlaceSourceType.zh };
  }
  return sourceOfData;
};

/**
 * Convert all form fields into accommodations api payload
 */
const _convertFormFieldsToAccommodationPayload = (values: CustomPropertyFormFields | HotelFormFields, accommodationsMyOwnLinkEnabled?: boolean) => {
  const payload: Omit<AddAccommodationPlaceV2Request, 'id' | 'sourceOfData'> | Omit<UpdateAccommodationPlaceV2Request, 'eventId' | 'id' | 'sourceOfData'> = {
    userNote: values.note || '',
    customPhotoId: values.customPhotoId,
    isFavorite: values.isFavorite || false,
    isHostStayingHere: values.isStayingHere || false,
    isPhotoHidden: !values.customPhotoId && !values.photoUrl,
    isCustomLinkEnabled: values.isCustomLinkEnabled || false,
    customUrl: accommodationsMyOwnLinkEnabled ? values.customUrl : values.isCustomUrl ? values.customUrl : '',
    customName: values.customName !== values.name ? values.customName : null,
    customAddress: values.customAddress !== values.address ? values.address : null,
    customPhone: values.customPhone !== values.phone ? values.phone : null,
    category: values.category ?? null,
    ...(values?.isHotelRoomBlock
      ? {
          accommodationRoomBlock: {
            bookingUrl: values.bookingUrl || '',
            checkInDate: formatDateForTravel(values.checkInDate),
            checkOutDate: formatDateForTravel(values.checkOutDate),
            cutoffDate: formatDateForTravel(values.cutoffDate),
            numberOfRoomsReserved: values.numberOfRoomsReserved || 0,
            pricePerNight: values.pricePerNight,
            isJoyManagedRoomBlock: Boolean(values?.isJoyManagedRoomBlock),
            strikeoutPricePerNight: values.strikeoutPricePerNight
          }
        }
      : {})
  };
  return payload;
};

/**
 * Convert all form fields into add accommodations api payload
 */
export const convertFormFieldsToAddAccommodationPayload = (eventId: string, values: CustomPropertyFormFields | HotelFormFields, accommodationsMyOwnLinkEnabled?: boolean) => {
  const payload = _convertFormFieldsToAccommodationPayload(values, accommodationsMyOwnLinkEnabled);
  const sourceOfData = convertAccommodationProviderIdToSourceOfData(values);
  const addAccommodationPayload: AddAccommodationPlaceV2Request = { ...payload, sourceOfData, eventId };
  return addAccommodationPayload;
};

/**
 * Convert all form fields into update accommodations api payload
 */
export const convertFormFieldsToUpdateAccommodationPayload = (id: string, values: CustomPropertyFormFields | HotelFormFields, accommodationsMyOwnLinkEnabled?: boolean) => {
  const payload = _convertFormFieldsToAccommodationPayload(values, accommodationsMyOwnLinkEnabled);
  const sourceOfData = convertAccommodationProviderIdToSourceOfData(values);
  const addAccommodationPayload: UpdateAccommodationPlaceV2Request = { ...payload, sourceOfData, id };
  return addAccommodationPayload;
};
